import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Modal,
  Box,
  TextField,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Pagination,
  CircularProgress,
} from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import config from '../config';

const server_URL = config.SERVER_URL;

const Tags = () => {
  const [open, setOpen] = useState(false);
  const [newTag, setNewTag] = useState({
    method: 'add',
    tag: '',
    customer_id: '',
    customer_user_ids: [],
  });
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerUsers, setCustomerUsers] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [loggedUserId, setLoggedUserId] = useState(null);
  const [customerFilter, setCustomerFilter] = useState('');
  const [toastState, setToastState] = useState({ open: false, message: '', severity: 'info' });
  const [tagError, setTagError] = useState('');
  const [customerUsersPage, setCustomerUsersPage] = useState(1);
  const [customerUsersTotalPages, setCustomerUsersTotalPages] = useState(1);
  const [customerUsersTotalRecords, setCustomerUsersTotalRecords] = useState(0);
  const [loadingCustomerUsers, setLoadingCustomerUsers] = useState(false);
  const customerUsersPerPage = 100; // Show 100 records per page

  const showToast = (message, severity = 'info') => {
    setToastState({ open: true, message, severity });
  };

  const validateTag = (tag) => {
    if (!tag) {
      setTagError('Tag is required');
      return false;
    }
    // Check if tag contains only allowed characters: A-Z, a-z, 0-9, hyphen, underscore
    const pattern = /^[A-Za-z0-9_-]+$/;
    if (!pattern.test(tag)) {
      setTagError('Tag can only contain letters (A-Z, a-z), numbers (0-9), hyphens (-), and underscores (_)');
      return false;
    }
    setTagError('');
    return true;
  };

  const handleOpen = () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const currentUserRole = userData.role_id || null;
    const currentUserId = userData.id || null;
    const customerId = currentUserRole === 2 ? (currentUserId ? Number(currentUserId) : '') : '';
    
    setNewTag({
      method: 'add',
      tag: '',
      customer_id: customerId,
      customer_user_ids: [],
    });
    setTagError('');
    setOpen(true);
    
    // Fetch customer users if customer_id is set
    // The useEffect will also trigger, but we call it explicitly to ensure it happens
    if (customerId) {
      setCustomerUsersPage(1); // Reset to page 1 when opening modal
      fetchCustomerUsers(customerId, 1);
    } else {
      setCustomerUsers([]);
      setCustomerUsersPage(1);
      setCustomerUsersTotalPages(1);
      setCustomerUsersTotalRecords(0);
    }
  };

  const handleClose = () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setNewTag({
      method: 'add',
      tag: '',
      customer_id: userRole === 2 ? loggedUserId : '',
      customer_user_ids: [],
    });
    setTagError('');
    setCustomerUsers([]);
    setOpen(false);
  };

  useEffect(() => {
    fetchTags();
    // Get logged user info
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(userData.role_id || null);
    setLoggedUserId(userData.id || null);
    // If role is 2, set selected customer to logged user (ensure it's a number)
    if (userData.role_id === 2 && userData.id) {
      setNewTag(prev => ({ ...prev, customer_id: Number(userData.id) }));
    }
  }, []);

  useEffect(() => {
    if (userRole !== null && loggedUserId !== null) {
      fetchCustomers();
    }
  }, [userRole, loggedUserId]);

  useEffect(() => {
    if (newTag.customer_id && newTag.customer_id !== '') {
      fetchCustomerUsers(newTag.customer_id);
    } else {
      setCustomerUsers([]);
    }
  }, [newTag.customer_id]);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(server_URL + '/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          method: 'list',
          user_id: loggedUserId,
          role_id: userRole
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customers');
      }

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setCustomers(data.data);
        
        // Auto-select if only one customer
        if (data.data.length === 1) {
          setNewTag(prev => ({ ...prev, customer_id: data.data[0].id }));
        }
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchCustomerUsers = async (customerId, page = 1) => {
    setLoadingCustomerUsers(true);
    try {
      const response = await fetch(server_URL + '/api/tags', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'get_customer_users',
          customer_id: customerId,
          page: page,
          per_page: customerUsersPerPage,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customer users');
      }

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setCustomerUsers(data.data || []);
        
        // Update pagination state
        if (data.pagination) {
          setCustomerUsersPage(data.pagination.current_page);
          setCustomerUsersTotalPages(data.pagination.total_pages);
          setCustomerUsersTotalRecords(data.pagination.total_records);
        }
      } else {
        setCustomerUsers([]);
        setCustomerUsersPage(1);
        setCustomerUsersTotalPages(1);
        setCustomerUsersTotalRecords(0);
      }
    } catch (error) {
      console.error('Error fetching customer users:', error);
      setCustomerUsers([]);
      setCustomerUsersPage(1);
      setCustomerUsersTotalPages(1);
      setCustomerUsersTotalRecords(0);
    } finally {
      setLoadingCustomerUsers(false);
    }
  };

  const handleCustomerUsersPageChange = (event, value) => {
    setCustomerUsersPage(value);
    if (newTag.customer_id) {
      fetchCustomerUsers(newTag.customer_id, value);
    }
  };

  const fetchTags = async (filterCustomerId = null) => {
    setIsLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = {
        method: 'list',
        user_id: userData.id || null,
        role_id: userData.role_id || null
      };
      
      // Add customer filter if provided
      if (filterCustomerId !== null && filterCustomerId !== '') {
        const customerIdValue = String(filterCustomerId) === '0' ? 0 : parseInt(filterCustomerId, 10);
        requestBody.customer_id = customerIdValue;
        console.log('Filtering by customer_id:', customerIdValue);
      }
      
      console.log('Request body:', requestBody);
      
      const response = await fetch(server_URL + '/api/tags', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tags');
      }

      const data = await response.json();
      console.log('Received tags data:', data);
      
      const tagsData = Array.isArray(data) ? data : data.data || [];
      setTags(tagsData);
      
      if (tagsData.length === 0) {
        console.log('No tags found matching the filter criteria');
      } else {
        console.log(`Found ${tagsData.length} tag(s)`);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      setTags([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleFilter = () => {
    fetchTags(customerFilter);
  };
  
  const handleClearFilter = () => {
    setCustomerFilter('');
    fetchTags();
  };

  const handleTagChange = (e) => {
    const tagValue = e.target.value;
    setNewTag(prev => ({ ...prev, tag: tagValue }));
    validateTag(tagValue);
  };

  const handleCustomerUserToggle = (customerUserId) => {
    setNewTag(prev => {
      const currentIds = prev.customer_user_ids || [];
      const isSelected = currentIds.includes(customerUserId);
      
      if (isSelected) {
        return {
          ...prev,
          customer_user_ids: currentIds.filter(id => id !== customerUserId)
        };
      } else {
        return {
          ...prev,
          customer_user_ids: [...currentIds, customerUserId]
        };
      }
    });
  };

  const handleSelectAll = () => {
    if (customerUsers.length === 0) return;
    
    const allIds = customerUsers.map(cu => cu.id);
    const currentIds = newTag.customer_user_ids || [];
    
    // If all are selected, deselect all; otherwise select all
    const allSelected = allIds.every(id => currentIds.includes(id));
    
    setNewTag(prev => ({
      ...prev,
      customer_user_ids: allSelected ? [] : allIds
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate tag
    if (!validateTag(newTag.tag)) {
      return;
    }
    
    // Validate customer selection
    if (!newTag.customer_id || newTag.customer_id === '') {
      showToast('Please select a customer', 'error');
      return;
    }
    
    setIsSubmitting(true);
    
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const tagData = {
      method: newTag.method,
      tag: newTag.tag,
      customer_id: newTag.customer_id,
      customer_user_ids: newTag.customer_user_ids || [],
      user_id: userData.id,
      is_subuser: userData.is_subuser || false,
      subuser_id: userData.subuser_id || null,
    };

    if (newTag.method === 'update') {
      tagData.id = newTag.id;
    }

    try {
      const response = await fetch(`${server_URL}/api/tags`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tagData),
      });

      const data = await response.json();
      
      if (data.status === '1') {
        await fetchTags(customerFilter);
        handleClose();
        showToast(data.message || 'Tag saved successfully', 'success');
      } else {
        showToast(data.error || 'Failed to save tag', 'error');
      }
    } catch (error) {
      console.error(`Error ${newTag.method}ing tag:`, error);
      showToast('An error occurred while saving the tag', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (id, tag, customer_id, customer_user_ids) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    // For role 2 users, ensure customer_id is set to logged user
    const finalCustomerId = userRole === 2 
      ? (loggedUserId || (customer_id !== undefined && customer_id !== null ? Number(customer_id) : ''))
      : (customer_id !== undefined && customer_id !== null ? Number(customer_id) : '');
    
    setNewTag({
      method: 'update',
      id,
      tag,
      customer_id: finalCustomerId,
      customer_user_ids: customer_user_ids || [],
    });
    setTagError('');
    setOpen(true);
    
    // Explicitly fetch customer users when editing
    // This ensures users are loaded even if customer_id hasn't changed from previous state
    if (finalCustomerId) {
      setCustomerUsersPage(1); // Reset to page 1 when editing
      fetchCustomerUsers(finalCustomerId, 1);
    } else {
      setCustomerUsers([]);
      setCustomerUsersPage(1);
      setCustomerUsersTotalPages(1);
      setCustomerUsersTotalRecords(0);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tag?')) {
      return;
    }

    setDeletingId(id);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/tags', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'delete',
          id: id,
          user_id: userData.id,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete tag');
      }

      const data = await response.json();
      if (data.status === '1') {
        await fetchTags(customerFilter);
        showToast('Tag deleted successfully', 'success');
      } else {
        showToast(data.error || 'Failed to delete tag', 'error');
      }
    } catch (error) {
      console.error('Error deleting tag:', error);
      showToast('Failed to delete tag', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const allSelected = customerUsers.length > 0 && customerUsers.every(cu => 
    (newTag.customer_user_ids || []).includes(cu.id)
  );
  const someSelected = customerUsers.some(cu => 
    (newTag.customer_user_ids || []).includes(cu.id)
  );

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h1>Tags</h1>
        <Button variant="contained" color="primary" onClick={handleOpen}>
          Add Tag
        </Button>
      </div>
      
      {/* Customer Filter - Only show for role 1 (admin) */}
      {userRole === 1 && (
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          alignItems: 'center', 
          mb: 3,
          p: 2,
          bgcolor: '#f5f5f5',
          borderRadius: 1
        }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Filter by Customer</InputLabel>
            <Select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              label="Filter by Customer"
              size="small"
            >
              <MenuItem value="">All Customers</MenuItem>
              {customers.filter(customer => customer.id !== 0).map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.username} ({customer.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button 
            variant="contained"
            onClick={handleFilter}
            sx={{ height: '40px' }}
          >
            Filter
          </Button>
          {customerFilter !== '' && (
            <Button 
              variant="outlined"
              color="warning"
              onClick={handleClearFilter}
              sx={{ height: '40px' }}
            >
              Clear Filter
            </Button>
          )}
          <Button 
            variant="outlined"
            onClick={() => fetchTags(customerFilter)}
            sx={{ height: '40px' }}
          >
            Refresh
          </Button>
        </Box>
      )}
      
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading tags...</p>
          </div>
        </div>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tag</TableCell>
                {userRole === 1 && <TableCell>Customer</TableCell>}
                <TableCell>Users Count</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tags.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={userRole === 1 ? 5 : 4} align="center" sx={{ py: 5 }}>
                    <Typography variant="h6" color="text.secondary">
                      No tags found
                    </Typography>
                    {customerFilter !== '' && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Try clearing the filter or selecting a different customer
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                tags.map((tag) => (
                  <TableRow key={tag.id} hover>
                    <TableCell>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {tag.tag}
                      </Typography>
                    </TableCell>
                    {userRole === 1 && (
                      <TableCell>
                        {tag.customer_username || 'N/A'}
                        {tag.customer_email && (
                          <Typography variant="caption" display="block" sx={{ color: '#666', fontSize: '0.75rem' }}>
                            {tag.customer_email}
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell>{tag.customer_user_count || 0}</TableCell>
                    <TableCell>
                      {tag.created_at ? new Date(tag.created_at).toLocaleString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => handleEdit(
                          tag.id,
                          tag.tag,
                          tag.customer_id,
                          tag.customer_user_ids
                        )}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(tag.id)}
                        disabled={deletingId === tag.id}
                      >
                        {deletingId === tag.id ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Deleting...
                          </>
                        ) : (
                          'Delete'
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="add-tag-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          <h2>{newTag.method === 'add' ? 'Add Tag' : 'Edit Tag'}</h2>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Customer</InputLabel>
                <Select
                  value={newTag.customer_id}
                  onChange={(e) => {
                    setNewTag(prev => ({ ...prev, customer_id: e.target.value, customer_user_ids: [] }));
                    setCustomerUsersPage(1); // Reset to page 1 when customer changes
                  }}
                  label="Customer"
                  disabled={userRole === 2} // Disable for role 2 users (only their own account)
                >
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.username} ({customer.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Tag"
                value={newTag.tag}
                onChange={handleTagChange}
                fullWidth
                required
                error={!!tagError}
                helperText={tagError || 'Only letters (A-Z, a-z), numbers (0-9), hyphens (-), and underscores (_) are allowed'}
                onBlur={() => validateTag(newTag.tag)}
              />

              {newTag.customer_id && (
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Select Users 
                      {customerUsersTotalRecords > 0 && (
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          ({customerUsersTotalRecords} total)
                        </Typography>
                      )}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleSelectAll}
                      disabled={loadingCustomerUsers}
                    >
                      {allSelected ? 'Deselect All' : 'Select All'}
                    </Button>
                  </Box>
                  
                  {loadingCustomerUsers ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
                      <CircularProgress size={40} />
                    </Box>
                  ) : customerUsers.length > 0 ? (
                    <>
                      <Box sx={{ maxHeight: '300px', overflow: 'auto', mb: 2 }}>
                        <List dense>
                          <ListItem disablePadding>
                            <ListItemButton onClick={handleSelectAll}>
                              <Checkbox
                                checked={allSelected}
                                indeterminate={someSelected && !allSelected}
                              />
                              <ListItemText primary="Select All (Current Page)" />
                            </ListItemButton>
                          </ListItem>
                          {customerUsers.map((customerUser) => {
                            // Build display text: "first_name last_name (email)"
                            const fullName = [customerUser.first_name, customerUser.last_name]
                              .filter(Boolean)
                              .join(' ')
                              .trim();
                            const displayText = fullName 
                              ? `${fullName} (${customerUser.email})`
                              : customerUser.email;
                            
                            return (
                              <ListItem key={customerUser.id} disablePadding>
                                <ListItemButton onClick={() => handleCustomerUserToggle(customerUser.id)}>
                                  <Checkbox
                                    checked={(newTag.customer_user_ids || []).includes(customerUser.id)}
                                  />
                                  <ListItemText 
                                    primary={displayText}
                                    secondary={customerUser.note || ''}
                                  />
                                </ListItemButton>
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>
                      
                      {/* Pagination Controls */}
                      {customerUsersTotalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                          <Typography variant="body2" color="text.secondary">
                            Page {customerUsersPage} of {customerUsersTotalPages}
                          </Typography>
                          <Pagination
                            count={customerUsersTotalPages}
                            page={customerUsersPage}
                            onChange={handleCustomerUsersPageChange}
                            color="primary"
                            size="small"
                            showFirstButton
                            showLastButton
                          />
                        </Box>
                      )}
                      
                      {customerUsersTotalPages === 1 && customerUsersTotalRecords > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
                          Showing all {customerUsersTotalRecords} user(s)
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No users found for this customer
                    </Typography>
                  )}
                </Box>
              )}


              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  type="submit"
                  disabled={isSubmitting || !newTag.tag || !newTag.customer_id}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {newTag.method === 'add' ? 'Adding...' : 'Updating...'}
                    </>
                  ) : (
                    newTag.method === 'add' ? 'Add Tag' : 'Update Tag'
                  )}
                </Button>
              </Box>
            </Stack>
          </form>
        </Box>
      </Modal>
      
      <Snackbar
        open={toastState.open}
        autoHideDuration={6000}
        onClose={() => setToastState(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToastState(prev => ({ ...prev, open: false }))}
          onClick={() => setToastState(prev => ({ ...prev, open: false }))}
          severity={toastState.severity}
          sx={{ width: '100%' }}
        >
          {toastState.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Tags;

